import React from "react";
import styled from "@emotion/styled";
import { css, SerializedStyles } from "@emotion/react";
import {
  text,
  colorPrimitives,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  spacing,
} from "@atlas-int/tokens";

// ─── Types ────────────────────────────────────────────────────────────────────

export type TextVariant =
  | "heading-1"
  | "heading-2"
  | "heading-3"
  | "heading-4"
  | "body"
  | "subsection"
  | "label"
  | "caption"
  | "overline";

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  variant?: TextVariant;
  children?: React.ReactNode;
}

export interface HeadingProps extends Omit<TextProps, "variant"> {
  level?: 1 | 2 | 3 | 4;
}

// ─── Default element mapping ─────────────────────────────────────────────────

const defaultElementMap: Record<TextVariant, React.ElementType> = {
  "heading-1": "h1",
  "heading-2": "h2",
  "heading-3": "h3",
  "heading-4": "h4",
  body: "p",
  subsection: "h3",
  label: "span",
  caption: "span",
  overline: "span",
};

// ─── Variant styles ──────────────────────────────────────────────────────────
// Matched exactly to the existing TextStyling.tsx values to avoid visual regression.

const variantStyles: Record<TextVariant, SerializedStyles> = {
  "heading-1": css`
    font-size: ${fontSize["5xl"]};
    line-height: ${lineHeight.tight};
    color: ${text.primary};
    font-weight: ${fontWeight.semibold};
    margin-bottom: ${spacing[8]};
  `,
  "heading-2": css`
    font-weight: ${fontWeight.semibold};
    font-size: 1.75rem;
    line-height: 1.14;
    color: ${text.primary};
    margin-top: ${spacing[24]};
    margin-bottom: ${spacing[8]};
  `,
  "heading-3": css`
    font-size: ${fontSize["2xl"]};
    line-height: ${lineHeight.tight};
    color: ${text.primary};
    font-weight: ${fontWeight.normal};
    margin-top: 2rem;
    margin-bottom: 0.5rem;
  `,
  "heading-4": css`
    font-weight: ${fontWeight.semibold};
    font-size: 0.9375rem;
    line-height: 1.4;
  `,
  body: css`
    font-size: ${fontSize.lg};
    line-height: ${lineHeight.normal};
    color: ${text.secondary};
    font-weight: ${fontWeight.normal};
    margin-bottom: ${spacing[8]};
    margin-top: 0;
  `,
  subsection: css`
    font-weight: ${fontWeight.normal};
    font-size: ${fontSize["2xl"]};
    letter-spacing: 0.04rem;
    color: ${colorPrimitives.blueSubheader};
    text-transform: uppercase;
  `,
  label: css`
    font-size: ${fontSize.sm};
    font-weight: ${fontWeight.medium};
    line-height: ${lineHeight.compact};
  `,
  caption: css`
    font-size: ${fontSize.xs};
    font-weight: ${fontWeight.normal};
    line-height: ${lineHeight.normal};
    color: ${text.muted};
  `,
  overline: css`
    font-size: ${fontSize.xs};
    font-weight: ${fontWeight.semibold};
    letter-spacing: ${letterSpacing.caps};
    text-transform: uppercase;
  `,
};

// ─── Styled element ──────────────────────────────────────────────────────────

const StyledText = styled.span<{ $variant: TextVariant }>`
  ${({ $variant }) => variantStyles[$variant]}
`;

// ─── Component ───────────────────────────────────────────────────────────────

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ as, variant = "body", children, ...rest }, ref) => {
    const Component = as ?? defaultElementMap[variant] ?? "p";

    return (
      <StyledText
        as={Component}
        ref={ref}
        $variant={variant}
        {...rest}
      >
        {children}
      </StyledText>
    );
  },
);

Text.displayName = "Text";

// ─── Heading convenience ─────────────────────────────────────────────────────

const Heading = React.forwardRef<HTMLElement, HeadingProps>(
  ({ level = 2, ...props }, ref) => {
    const variant = `heading-${level}` as TextVariant;
    return <Text ref={ref} variant={variant} {...props} />;
  },
);

Heading.displayName = "Heading";

// ─── Named style exports ────────────────────────────────────────────────────
// These allow consumers to use the raw css`` values (e.g. in interpolations).

export const heading1Style = variantStyles["heading-1"];
export const heading2Style = variantStyles["heading-2"];
export const heading3Style = variantStyles["heading-3"];
export const heading4Style = variantStyles["heading-4"];
export const bodyStyle = variantStyles["body"];
export const subsectionStyle = variantStyles["subsection"];

/* ------------------------------------------------------------------ */
/*  Convenience aliases -- drop-in replacements for legacy components  */
/* ------------------------------------------------------------------ */

/** Renders an <h1> with heading-1 styles. */
export const H1 = styled.h1`
  ${variantStyles["heading-1"]}
`;

/** Renders an <h2> with heading-2 styles. */
export const H2 = styled.h2`
  ${variantStyles["heading-2"]}
`;

/** Renders an <h3> with heading-3 styles. */
export const H3 = styled.h3`
  ${variantStyles["heading-3"]}
`;

/** Renders a <p> with heading-4 styles (matches legacy H4 which used styled.p). */
export const H4 = styled.p`
  ${variantStyles["heading-4"]}
`;

/** Renders a <p> with body text styles (replaces legacy Paragraph). */
export const Paragraph = styled.p`
  ${variantStyles["body"]}
`;

/** Renders a <p> with subsection header styles (replaces legacy SubSectionHeader). */
export const SubSectionHeader = styled.p`
  ${variantStyles["subsection"]}
`;

// ─── Exports ─────────────────────────────────────────────────────────────────

export default Text;
export { Text, Heading, variantStyles };
